import { useState } from 'react';
import { X, ChevronLeft, Film } from 'lucide-react';
import { MovieSearch } from '../../ui/MovieSearch';
import type { MovieSearchResult } from '../../../lib/tmdb';
import { cn } from '../../../lib/utils';

interface Props {
  genres: string[];
  onSelect: (favorites: string[]) => void;
  onBack: () => void;
  showBackButton: boolean;
}

interface FavoriteMovie {
  id: number;
  title: string;
  year: number;
  posterUrl: string | null;
}

export function FavoriteMovies({ onSelect, onBack, showBackButton }: Props) {
  const [favorites, setFavorites] = useState<FavoriteMovie[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleAddFavorite = (movie: MovieSearchResult) => {
    if (favorites.length >= 3) {
      setError('You can only add up to 3 favorite movies');
      return;
    }

    if (favorites.some(f => f.id === movie.id)) {
      setError('This movie is already in your favorites');
      return;
    }

    setFavorites(prev => [...prev, {
      id: movie.id,
      title: movie.title,
      year: movie.year,
      posterUrl: movie.posterPath
    }]);
    setError(null);
  };

  const handleRemoveFavorite = (movieId: number) => {
    setFavorites(prev => prev.filter(m => m.id !== movieId));
    setError(null);
  };

  const handleSubmit = () => {
    if (favorites.length === 0) {
      setError('Please add at least one favorite movie');
      return;
    }
    onSelect(favorites.map(f => f.title));
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2 text-center">
        <h2 className="display text-2xl font-semibold sm:text-3xl">
          Name a few movies you <span className="text-gradient-dusk">loved</span>.
        </h2>
        <p className="text-muted-foreground">
          Add up to 3 — they teach us your taste better than anything else.
        </p>
        <p className="text-sm text-muted-foreground/80">
          <span className={cn('font-semibold', favorites.length > 0 ? 'text-primary' : 'text-muted-foreground')}>
            {favorites.length} of 3
          </span>{' '}
          added
        </p>
      </div>

      <div className="space-y-4">
        <MovieSearch
          onSelect={handleAddFavorite}
          placeholder="Search for a movie you enjoyed..."
        />

        <div className="space-y-2.5">
          {favorites.map((movie, i) => (
            <div
              key={movie.id}
              className="panel-raised animate-rise flex items-center gap-4 p-3 pr-4"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="poster-frame h-[72px] w-12 shrink-0 !rounded-lg">
                {movie.posterUrl ? (
                  <img
                    src={movie.posterUrl}
                    alt={movie.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Film className="h-5 w-5 text-muted-foreground/50" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-foreground">{movie.title}</div>
                <div className="text-sm text-muted-foreground">{movie.year}</div>
              </div>
              <button
                onClick={() => handleRemoveFavorite(movie.id)}
                className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground"
                aria-label={`Remove ${movie.title}`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>

        {error && (
          <p className="text-center text-sm text-red-400">{error}</p>
        )}
      </div>

      <div className="flex items-center gap-3">
        {showBackButton && (
          <button onClick={onBack} className="btn-ghost px-5 py-3">
            <ChevronLeft className="h-5 w-5" />
            Back
          </button>
        )}
        <button onClick={handleSubmit} className="btn-marquee flex-1 px-4 py-3.5 text-base">
          Continue
        </button>
      </div>
    </div>
  );
}
